import type {
  ChatLlmStreamRequest,
  ChatLlmStreamResult,
  AgentRunDoc,
  DataFileToDownload,
  DataFileToUpload,
  LlmStreamDoc,
  RunAgentRequest,
  RunAgentResult,
  RunVerifierRequest,
  RunVerifierResult,
  TaskReviewReportDoc,
  UploadedDataFile,
} from './hostTypes';
import { createLlmApi, createVerifierApi } from '../domain/llm/api';
import { trackPendingOperation } from '../services/pendingOperations';

export type {
  ChatLlmStreamRequest as TaskTypeLlmStreamRequest,
  ChatLlmStreamResult as TaskTypeLlmStreamResult,
  AgentCreatedFile,
  AgentRawCodexReference,
  AgentRunDoc,
  AgentRunStatus,
  RunAgentRequest,
  RunAgentResult,
  DataFileToDownload as TaskTypeFileToDownload,
  DataFileToUpload as TaskTypeFileToUpload,
  LlmAttachment as TaskTypeLlmAttachment,
  LlmAttachmentMimeType as TaskTypeLlmAttachmentMimeType,
  LlmModelId as TaskTypeLlmModelId,
  LlmStreamDoc as TaskTypeLlmStreamDoc,
  RunVerifierRequest as TaskTypeVerifierRequest,
  RunVerifierResult as TaskTypeVerifierResult,
  VerifierLanguage as TaskTypeVerifierLanguage,
  VerifierStatus as TaskTypeVerifierStatus,
  UploadedDataFile as TaskTypeUploadedFile,
  TaskReviewReportDoc as TaskTypeReviewReport,
  TaskReviewReportStatus as TaskTypeReviewReportStatus,
} from './hostTypes';

export type TaskTypeUnsubscribe = () => void;
export type TaskTypeErrorMetadata = Record<string, unknown>;

export interface TaskTypeHost {
  identity: {
    currentUserId: () => string | null;
  };
  llm: {
    createStreamDocId: () => Promise<string>;
    subscribeToStream: (
      streamDocId: string,
      callback: (stream: LlmStreamDoc | null) => void,
    ) => Promise<TaskTypeUnsubscribe>;
    startStream: (request: ChatLlmStreamRequest) => Promise<ChatLlmStreamResult>;
  };
  agent: {
    createRunId: () => string;
    subscribeToRun: (
      runId: string,
      callback: (run: AgentRunDoc | null) => void,
    ) => Promise<TaskTypeUnsubscribe>;
    run: (request: RunAgentRequest) => Promise<RunAgentResult>;
    downloadRawCodex: (runId: string) => Promise<Uint8Array>;
    /** Full bytes of a created file whose entry is `downloadable`. */
    downloadFile: (runId: string, path: string) => Promise<Uint8Array>;
  };
  verifier: {
    run: (request: RunVerifierRequest) => Promise<RunVerifierResult>;
  };
  files: {
    upload: (request: {
      prefix: string;
      file: DataFileToUpload;
      onProgress?: (bytesTransferred: number) => void;
    }) => Promise<UploadedDataFile>;
    downloadBytes: (file: DataFileToDownload) => Promise<Uint8Array>;
    downloadText: (file: DataFileToDownload) => Promise<string>;
    downloadAsZip: (files: DataFileToDownload[]) => Promise<Blob>;
  };
  /**
   * Server-built AI review reports (`taskReviewReports/{taskId}`). Readable
   * by admins and campaign reviewers only; an expert's subscription is
   * denied, so task types gate this on `viewerMode`/`isAdminViewer`.
   */
  reviewReports: {
    subscribe: (
      taskId: string,
      callback: (report: TaskReviewReportDoc | null) => void,
      onError?: (error: Error) => void,
    ) => Promise<TaskTypeUnsubscribe>;
  };
  errors: {
    report: (error: unknown, context: string, metadata?: TaskTypeErrorMetadata) => void;
  };
}

/**
 * The service adapters intentionally live behind this module. Task views only
 * import the host contract; they never import Firebase clients or the global
 * LLM/verifier/file services. The dynamic imports keep those adapters out of
 * catalog-only routes until a selected runtime actually invokes a capability.
 */
export function createTaskTypeHost(currentUserId: string | null = null): TaskTypeHost {
  let llmApiPromise: Promise<ReturnType<typeof createLlmApi>> | null = null;
  const getLlmApi = () => {
    llmApiPromise ??= import('../platform/firebase/llmGateway').then(({ firebaseLlmGateway }) => (
      createLlmApi(firebaseLlmGateway)
    ));
    return llmApiPromise;
  };
  let verifierApiPromise: Promise<ReturnType<typeof createVerifierApi>> | null = null;
  const getVerifierApi = () => {
    verifierApiPromise ??= import('../platform/firebase/verifierGateway').then(({ firebaseVerifierGateway }) => (
      createVerifierApi(firebaseVerifierGateway)
    ));
    return verifierApiPromise;
  };
  let agentApiPromise: Promise<Awaited<typeof import('../platform/firebase/agentGateway')>['firebaseAgentGateway']> | null = null;
  const getAgentApi = () => {
    agentApiPromise ??= import('../platform/firebase/agentGateway').then(({ firebaseAgentGateway }) => firebaseAgentGateway);
    return agentApiPromise;
  };
  const newRunId = () => {
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replaceAll('-', '')
      : `${Date.now()}${Math.random().toString(36).slice(2)}`;
    return `hr_${uuid.slice(0, 32)}`;
  };
  const reportError = (error: unknown, context: string, metadata?: TaskTypeErrorMetadata) => {
    void import('../utils/globalErrorLogger').then(({ logGlobalError }) => {
      logGlobalError(error, { context, metadata });
    });
  };
  return {
    identity: {
      currentUserId: () => currentUserId,
    },
    llm: {
      createStreamDocId: async () => {
        return (await getLlmApi()).createStreamDocId();
      },
      subscribeToStream: async (streamDocId, callback) => {
        return (await getLlmApi()).subscribeToStream(streamDocId, callback);
      },
      startStream: async (request) => {
        return (await getLlmApi()).startStream(request);
      },
    },
    agent: {
      createRunId: newRunId,
      subscribeToRun: async (runId, callback) => (await getAgentApi()).subscribeToRun(runId, callback),
      run: async (request) => (await getAgentApi()).run(request),
      downloadRawCodex: async (runId) => (await getAgentApi()).downloadRawCodex(runId),
      downloadFile: async (runId, path) => (await getAgentApi()).downloadFile(runId, path),
    },
    verifier: {
      run: async (request) => {
        return (await getVerifierApi()).run(request);
      },
    },
    files: {
      upload: ({ prefix, file, onProgress }) => trackPendingOperation((async () => {
        const uid = currentUserId;
        if (!uid) throw new Error('Sign in before uploading task files.');
        const { uploadDataFile } = await import('../services/dataFileUploads');
        return uploadDataFile(uid, prefix, file, onProgress);
      })()),
      downloadAsZip: async (files) => {
        const { downloadDataFilesAsZip } = await import('../services/dataFileUploads');
        return downloadDataFilesAsZip(files);
      },
      downloadBytes: async (file) => {
        const { downloadDataFileBytes } = await import('../services/dataFileUploads');
        return downloadDataFileBytes(file);
      },
      downloadText: async (file) => {
        const { downloadDataFileText } = await import('../services/dataFileUploads');
        return downloadDataFileText(file);
      },
    },
    reviewReports: {
      subscribe: async (taskId, callback, onError) => {
        const { subscribeToTaskReviewReport } = await import('../services/taskReviewReports');
        // Stream errors are logged here, not in the service; see the note on
        // `subscribeToTaskReviewReport`.
        return subscribeToTaskReviewReport(taskId, callback, (error) => {
          reportError(error, 'taskReviewReports.subscribeToTaskReviewReport', { taskId });
          onError?.(error);
        });
      },
    },
    errors: {
      report: reportError,
    },
  };
}
